import { memo, useMemo } from "react";
import { Pressable, PressableProps, StyleSheet } from "react-native";

import {
  BMBadgeTag,
  BMComponentsType,
  BMPrimitives,
  BMSkeletonLoader,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcPaymentRefund16 from "@traveloka/icon-kit-web/react/IcPaymentRefund16";
import IcSystemChevronDown16 from "@traveloka/icon-kit-web/react/IcSystemChevronDown16";
import IcSystemStatusInfoFill16 from "@traveloka/icon-kit-web/react/IcSystemStatusInfoFill16";
import IcTripOneWay from "@traveloka/icon-kit-web/react/IcTripOneWay";

import IcSystemStatusWarning16 from "@traveloka/icon-kit-web/react/IcSystemStatusWarning16";
import { ViewProps } from "../../../../components";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../utils";
import FlightAirlineLogos from "../FlightAirlineLogos/FlightAirlineLogos";
import { FlightRefundRescheduleBadge } from "../FlightRefundRescheduleBadge";
import { FlightSummaryComponentProps } from "./types";

function FlightSummary({
  type,
  summary: {
    airlineLogos,
    departureDetail,
    arrivalDetail,
    dateOffset,
    duration,
    numOfTransit,
    seatClass,
    subClass,
  },
  isCompliant,
  refundable,
  onClick,
}: FlightSummaryComponentProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const { CorporateFlightProductSummary: cr } = useMobileContentResource();
  return (
    <BMView>
      {!isCompliant ? (
        <BMView style={styles.nonCompliantBadgeWrapper}>
          <BMView style={styles.nonCompliantBadgeContainer} row align="center">
            <IcSystemStatusWarning16 color={BMComponents.BMUiBadgeInkYellow} />
          </BMView>
        </BMView>
      ) : null}
      <BMView
        style={[styles.container, !isCompliant ? styles.paddingTopLg : null]}
        spacing="md"
      >
        <BMView row spacing="xl">
          <BMView spacing="sm">
            <BMBadgeTag label={cr[`ENUM_${type}`]} type="GREEN" />
            <FlightAirlineLogos logos={airlineLogos} />
          </BMView>

          <BMView spacing="xs">
            <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
              {formatMessage(cr.flightDepartureArrivalCity, {
                departureCity: departureDetail.airport.city,
                arrivalCity: arrivalDetail.airport.city,
              })}
            </BMText>

            <BMView row spacing="sm" align="center">
              <BMView row spacing="xs" align="center">
                <BMText variant="uiHuge" ink="BMStyleTextInkPrimary">
                  {departureDetail.airport.airportCode}
                </BMText>
                <IcTripOneWay
                  width={16}
                  height={16}
                  color={BMComponents.BMStyleIconInkPrimary}
                  accentColor={BMComponents.BMStyleIconInkPrimary}
                />
                <BMText variant="uiHuge" ink="BMStyleTextInkPrimary">
                  {arrivalDetail.airport.airportCode}
                </BMText>
              </BMView>
              <BMView style={styles.divider} />
              <BMText variant="uiLarge" ink="BMStyleTextInkPrimary">
                {departureDetail.departureDate}
              </BMText>
            </BMView>

            <BMView row spacing="sm" align="center">
              <BMText variant="uiSmall" ink="BMStyleTextInkPrimary">
                {departureDetail.departureTime} - {arrivalDetail.arrivalTime}
                {dateOffset
                  ? ` ${formatMessage(cr.dateOffset, { num: dateOffset })}`
                  : ""}
              </BMText>
              <BMView style={styles.divider} />
              <BMText variant="uiSmall" ink="BMStyleTextInkPrimary">
                {duration}
              </BMText>
              <BMView row spacing="sm" align="center">
                <BMView style={styles.divider} />
                <BMView row spacing="xs" align="center">
                  <BMText variant="uiSmall" ink="BMStyleTextInkPrimary">
                    {formatMessage(cr.numOfTransit, { num: numOfTransit })}
                  </BMText>
                  <IcSystemStatusInfoFill16
                    width={16}
                    height={16}
                    color={BMComponents.BMStyleIconInkAction}
                  />
                </BMView>
              </BMView>
            </BMView>

            <BMView>
              <BMView row spacing="sm" align="center">
                <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
                  {seatClass}
                </BMText>
                {subClass ? (
                  <BMView row spacing="sm" align="center">
                    <BMView style={styles.divider} />
                    <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
                      {subClass}
                    </BMText>
                  </BMView>
                ) : null}
              </BMView>
            </BMView>
          </BMView>
        </BMView>
        <FlightSummaryFooter refundable={refundable} onClick={onClick} />
      </BMView>
    </BMView>
  );
}

type FlightSummaryFooterProps = Pick<
  FlightSummaryComponentProps,
  "refundable" | "onClick"
>;

export const FlightSummaryFooter = memo(function FlightSummaryFooter({
  refundable,
  onClick,
}: FlightSummaryFooterProps) {
  const { BMComponents } = useBMTheme();
  const { CorporateFlightProductSummary: cr } = useMobileContentResource();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const shouldRender = Boolean(refundable) || Boolean(onClick);
  if (!shouldRender) return null;

  const Container = (onClick ? Pressable : BMView) as
    | React.ComponentType<PressableProps>
    | React.ComponentType<ViewProps>;

  const containerProps = onClick ? { onPress: onClick } : {};

  const ChevronIcon = (
    <BMView style={styles.icon} justify="center" align="center">
      <IcSystemChevronDown16
        width={12}
        height={12}
        color={BMComponents.BMStyleTextInkPrimary}
      />
    </BMView>
  );

  if (!refundable) {
    return (
      <Container {...containerProps}>
        <BMView row align="center" justify="end">
          {ChevronIcon}
        </BMView>
      </Container>
    );
  }

  return (
    <Container {...containerProps}>
      <BMView row align="center" justify="between">
        {refundable.isLoading && <BMSkeletonLoader width={100} height={22} />}

        {refundable.status && (
          <BMView flex row spacing="sm">
            <FlightRefundRescheduleBadge
              variant="REFUND"
              status={refundable.status}
            />
          </BMView>
        )}

        {!refundable.isLoading && !refundable.status && (
          <BMView
            row
            spacing="xs"
            align="center"
            style={styles.paddingVertical3}
          >
            <IcPaymentRefund16
              width={16}
              height={16}
              color={BMComponents.BMStyleTextInkSecondary}
            />
            <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
              {cr.refundInfoErrorCaption}
            </BMText>
            <Pressable
              onPress={refundable.onRetry}
              accessibilityRole="button"
              accessibilityLabel="Retry"
            >
              <BMText variant="uiSmallLi" ink="BMStyleTextInkAction">
                {cr.refundInfoErrorCta}
              </BMText>
            </Pressable>
          </BMView>
        )}

        {onClick ? ChevronIcon : null}
      </BMView>
    </Container>
  );
});

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    paddingTopLg: {
      paddingTop: BMPrimitives.BMSize.lg,
    },
    container: {
      padding: BMPrimitives.BMSize.md,
    },
    icon: {
      width: 16,
      height: 16,
      borderRadius: BMPrimitives.BMBorder.radius.rounded,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorActive,
    },
    divider: {
      borderLeftWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      height: 12,
    },
    paddingVertical3: {
      paddingVertical: 3,
    },
    nonCompliantBadgeContainer: {
      padding: 3,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorWarning,
      borderEndStartRadius: BMPrimitives.BMRadius.sm,
    },
    nonCompliantBadgeWrapper: {
      top: 0,
      right: 0,
      zIndex: 1,
      position: "absolute",
    },
  });
}

export default memo(FlightSummary);
